/**
 * The break-glass audit (D20): every offline sign-in the tenant's nodes
 * recorded while cut off, newest first — when, where, how (offline PIN or the
 * emergency account), the outcome, the account typed and the source address,
 * never a secret. Filters (kind, outcome, site, free text) apply to the pages
 * loaded so far; "Load older" fetches the next page.
 */
import type { AuditEntryWire } from '@acp/api-contract/offlineAuth.js';
import { RefreshCwIcon } from 'lucide-react';
import { type AuditFilter, filterAudit } from '../../lib/admin/view.js';
import { UNKNOWN_HINT, UNKNOWN_LABEL } from '../../lib/openEnum.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type AuditTableProps = {
  readonly entries: readonly AuditEntryWire[];
  readonly sites: readonly { readonly id: string; readonly name: string }[];
  readonly filter: AuditFilter;
  readonly loading: boolean;
  readonly error: string | null;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly onFilterChange: (filter: AuditFilter) => void;
  readonly onLoadMore: () => void;
  readonly onRetry: () => void;
};

// `unknown`: a kind or outcome a newer node reported that this build does not know — neutral (D37).
const OUTCOME_BADGE = { success: 'success', failure: 'destructive', locked: 'warning', 'refused-online': 'outline', 'rate-limited': 'warning', unknown: 'secondary' } as const;
const KINDS = ['offline-pin', 'emergency', 'forced-promotion'] as const;
const KIND_LABEL = { 'offline-pin': 'offline PIN', emergency: 'emergency account', 'forced-promotion': 'standby forced on air', unknown: UNKNOWN_LABEL } as const;
const OUTCOMES = ['success', 'failure', 'locked', 'refused-online', 'rate-limited'] as const;

function isKind(value: string): value is AuditFilter['kind'] {
  return value === 'all' || KINDS.some((k) => k === value);
}

function isOutcome(value: string): value is AuditFilter['outcome'] {
  return value === 'all' || OUTCOMES.some((o) => o === value);
}

export function AuditTable({ entries, sites, filter, loading, error, hasMore, loadingMore, onFilterChange, onLoadMore, onRetry }: AuditTableProps) {
  const shown = filterAudit(entries, filter);
  const siteName = new Map(sites.map((s) => [s.id, s.name]));
  return (
    <Card data-testid="audit-table">
      <CardHeader>
        <CardTitle>Audit log</CardTitle>
        <CardDescription>Break-glass sign-ins on your servers while they could not reach the cloud (offline PINs and emergency accounts), and standbys an operator forced on air, reported when the link came back. Kept one year.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="grid gap-1">
            <Label htmlFor="audit-kind">Kind</Label>
            <NativeSelect id="audit-kind" value={filter.kind} onChange={(e) => (isKind(e.target.value) ? onFilterChange({ ...filter, kind: e.target.value }) : undefined)}>
              <option value="all">All</option>
              <option value="offline-pin">Offline PIN</option>
              <option value="emergency">Emergency account</option>
              <option value="forced-promotion">Standby forced on air</option>
            </NativeSelect>
          </div>
          <div className="grid gap-1">
            <Label htmlFor="audit-outcome">Outcome</Label>
            <NativeSelect id="audit-outcome" value={filter.outcome} onChange={(e) => (isOutcome(e.target.value) ? onFilterChange({ ...filter, outcome: e.target.value }) : undefined)}>
              <option value="all">All</option>
              {OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1">
            <Label htmlFor="audit-site">Site</Label>
            <NativeSelect id="audit-site" value={filter.siteId} onChange={(e) => onFilterChange({ ...filter, siteId: e.target.value })}>
              <option value="all">All</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1">
            <Label htmlFor="audit-text">Account, address or node</Label>
            <Input id="audit-text" value={filter.text} onChange={(e) => onFilterChange({ ...filter, text: e.target.value })} data-testid="audit-text" />
          </div>
        </div>
        {error === null ? null : (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
            <span className="text-destructive">Could not load the audit: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        )}
        {loading && entries.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p> : null}
        {!loading && error === null && shown.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="audit-empty">
            {entries.length === 0 ? 'No break-glass sign-ins recorded.' : 'Nothing matches the filter in what is loaded.'}
          </p>
        ) : null}
        {shown.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When (UTC)</TableHead>
                <TableHead>Site</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((e) => (
                <TableRow key={`${e.auditId}-${e.at}`} data-testid="audit-row">
                  <TableCell className="font-mono text-xs">{e.at.slice(0, 19).replace('T', ' ')}</TableCell>
                  <TableCell className="text-sm">{siteName.get(e.siteId) ?? e.siteId}</TableCell>
                  <TableCell className="text-sm">
                    {KIND_LABEL[e.kind]}
                    {e.detail === undefined || e.detail === null ? null : <div className="text-xs text-muted-foreground">{e.detail}</div>}
                  </TableCell>
                  <TableCell>
                    <Badge variant={OUTCOME_BADGE[e.outcome]} title={e.outcome === 'unknown' ? UNKNOWN_HINT : undefined}>
                      {e.outcome === 'unknown' ? UNKNOWN_LABEL : e.outcome}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{e.account}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {e.sourceIp ?? '–'} · node {e.nodeId.slice(-6)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
        {hasMore ? (
          <Button variant="outline" className="w-fit" disabled={loadingMore} onClick={onLoadMore} data-testid="audit-more">
            {loadingMore ? 'Loading…' : 'Load older'}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
